import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Truck, ClipboardCheck, DollarSign, AlertTriangle, Radio, Clock, CheckCircle, AlertCircle, ArrowRight, RotateCcw, Copy, Archive, Building2, Shield, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [attentionOrders, setAttentionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, attentionRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/dashboard/attention`),
      ]);
      setSummary(summaryRes.data);
      setAttentionOrders(attentionRes.data);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (orderId) => {
    setApproving(orderId);
    setMessage(null);
    try {
      await axios.post(`${API}/orders/${orderId}/approve-dispatch`);
      setAttentionOrders(prev => prev.filter(o => o.id !== orderId));
      setMessage({ type: "success", text: "Order approved for Dispatch" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to approve" });
    } finally {
      setApproving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5D8A]" />
      </div>
    );
  }

  const s = summary || {};
  const ct = s.company_totals || {};
  const ex = s.exceptions || {};
  const sched = s.scheduling || {};
  const disp = s.dispatch || {};
  const qa = s.qa || {};
  const fin = s.finance || {};
  const day1 = s.day1_status;

  const formatResetDate = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return isoStr; }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <h1 className="text-xl font-bold text-gray-800" data-testid="dashboard-title">Dashboard</h1>

      {day1 && (
        <div data-testid="day1-mode-banner" className="relative overflow-hidden rounded-lg border border-[#1E3A5F]/30 bg-gradient-to-r from-[#1E3A5F] via-[#162d4a] to-[#1E3A5F] px-4 py-3">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
                <RotateCcw className="h-4 w-4 text-white/80" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1F2937] tracking-wide">DAY 1 MODE ACTIVE</p>
                <p className="text-xs text-[#1F2937]/50">
                  Sistema resetado em {formatResetDate(day1.activated_at)}
                  {" — "}dados anteriores ao reset estao filtrados
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">
              <span className="h-1.5 w-1.5 rounded-full bg-white/80 animate-pulse" />
              <span className="text-xs font-medium text-[#1F2937]/80">Point Zero</span>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`p-2.5 rounded-lg flex items-center gap-2 text-sm ${message.type === "success" ? "bg-[#EDF3EC] border border-[#C5D9C1] text-[#4A7A56]" : "bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545]"}`}>
          {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Company-Wide Totals */}
      <div data-testid="company-totals-section">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Company Overview</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <TotalCard
            label="Total Active Orders"
            value={ct.total_orders || 0}
            icon={Building2}
            color="white"
            testId="total-orders-card"
          />
          <TotalCard
            label="Safeguard Orders"
            value={ct.total_safeguard || 0}
            icon={Shield}
            color="blue"
            testId="total-safeguard-card"
          />
          <TotalCard
            label="National Orders"
            value={ct.total_national || 0}
            icon={Globe}
            color="emerald"
            testId="total-national-card"
          />
        </div>
      </div>

      {/* Exception / Tracking Cards */}
      <div data-testid="exceptions-section">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Order Tracking</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ExceptionCard
            label="Removed from Source"
            total={ex.removed_by_safeguard?.total || 0}
            today={ex.removed_by_safeguard?.today || 0}
            icon={AlertTriangle}
            color="red"
            onClick={() => navigate("/archive")}
            testId="exception-removed"
          />
          <ExceptionCard
            label="Duplicate Orders"
            total={ex.duplicates?.total || 0}
            today={ex.duplicates?.today || 0}
            icon={Copy}
            color="amber"
            testId="exception-duplicates"
          />
          <ExceptionCard
            label="Returned Orders"
            total={ex.returned_orders?.total || 0}
            today={ex.returned_orders?.today || 0}
            icon={RotateCcw}
            color="cyan"
            testId="exception-returned"
          />
        </div>
      </div>

      {/* Main Operational Queues */}
      <div data-testid="queues-section">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Operational Queues</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <SummaryCard
            title="Scheduling"
            icon={Calendar}
            color="amber"
            total={sched.total || 0}
            onClick={() => navigate("/scheduling")}
            items={[
              { label: "Internal Calls", value: sched.internal || 0 },
              { label: "External Calls", value: sched.external || 0 },
              { label: "National Calls", value: sched.national || 0 },
              { label: "Urgent Calls", value: sched.urgent || 0, highlight: sched.urgent > 0 },
            ]}
            testId="scheduling-card"
          />
          <SummaryCard
            title="Dispatch"
            icon={Truck}
            color="blue"
            total={disp.total || 0}
            onClick={() => navigate("/dispatch")}
            items={[
              { label: "Regular", value: disp.regular || 0 },
              { label: "Appointment Set", value: disp.appointment_set || 0 },
              { label: "External Dispatched", value: disp.ext_dispatched || 0 },
            ]}
            testId="dispatch-card"
          />
          <SummaryCard
            title="Quality Assurance"
            icon={ClipboardCheck}
            color="cyan"
            total={qa.total || 0}
            onClick={() => navigate("/qa")}
            items={[
              { label: "Awaiting Review", value: qa.awaiting || 0 },
              { label: "Approved", value: qa.approved || 0 },
            ]}
            testId="qa-card"
          />
          <SummaryCard
            title="Finance"
            icon={DollarSign}
            color="emerald"
            total={fin.total || 0}
            onClick={() => navigate("/finance")}
            items={[
              { label: "Ready for Billing", value: fin.ready || 0 },
              { label: "Billing", value: fin.billing || 0 },
              { label: "Paid", value: fin.paid || 0 },
            ]}
            testId="finance-card"
          />
        </div>
      </div>

      {/* Global Operational Map - moved to Scheduling */}

      {/* Attention Sections */}
      <div data-testid="attention-badges-section">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Attention Required</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <AttentionBadge
            title="PHOT / Follow-Up"
            count={s.attention?.phot || 0}
            icon={AlertTriangle}
            color="amber"
            description="Orders needing manual review"
            testId="attention-phot"
          />
          <AttentionBadge
            title="Follow-Up"
            count={s.follow_up?.count || 0}
            icon={ClipboardCheck}
            color="violet"
            description="Orders in follow-up status"
            testId="attention-followup"
          />
          <AttentionBadge
            title="Pending Transmission"
            count={s.transmission?.pending || 0}
            icon={Radio}
            color="orange"
            description="Orders not yet transmitted"
            testId="attention-transmission"
          />
          <AttentionBadge
            title="Pending Closing"
            count={s.closing?.pending || 0}
            icon={Clock}
            color="rose"
            description="Completed inspections awaiting EOD close"
            testId="attention-closing"
          />
          <AttentionBadge
            title="EOD Updated"
            count={s.eod_updates?.updated || 0}
            icon={CheckCircle}
            color="emerald"
            description="Orders with End of Day update today"
            testId="attention-eod-done"
          />
          <AttentionBadge
            title="EOD Missing"
            count={s.eod_updates?.missing || 0}
            icon={AlertTriangle}
            color="orange"
            description="Orders worked today without EOD update"
            testId="attention-eod-missing"
          />
        </div>
      </div>

      {/* PHOT Attention Orders Table */}
      <div className="bg-white rounded-lg border border-gray-200" data-testid="attention-section">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#9A7A3E] uppercase tracking-wide">Follow-Up Attention</h2>
            <p className="text-gray-400 text-xs mt-0.5">PHOT orders requiring manual review before Dispatch</p>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#EDE5D6] text-[#9A7A3E] border border-[#DDD3BF]" data-testid="attention-count">
            {attentionOrders.length}
          </span>
        </div>

        {attentionOrders.length === 0 ? (
          <div className="py-10 text-center">
            <CheckCircle className="h-7 w-7 text-[#6B8F62] mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No orders need attention</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attention-table">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="text-left px-3 py-2.5 font-medium text-xs">Due Date</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">Order #</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">Company</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">Type</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">Client</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">City</th>
                  <th className="text-left px-3 py-2.5 font-medium text-xs">State</th>
                  <th className="text-right px-3 py-2.5 font-medium text-xs">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50">
                {attentionOrders.map(order => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    data-testid={`attention-row-${order.id}`}
                    onClick={() => navigate(`/order/${order.id}`)}
                  >
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.due_date || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-800 font-medium whitespace-nowrap">{order.order_number || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.company || "-"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EDE5D6] text-[#9A7A3E]">{order.order_type || "-"}</span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.cliente || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.city || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.state || "-"}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleApprove(order.id)}
                        disabled={approving === order.id}
                        className="px-3 py-1.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                        data-testid={`approve-btn-${order.id}`}
                      >
                        {approving === order.id ? "..." : "Approve for Dispatch"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekend Call Photos - moved to Scheduling */}
    </div>
  );
};

/* Company Total Card */
const TotalCard = ({ label, value, icon: Icon, color, testId }) => {
  const colorMap = {
    white: { bg: "bg-white", border: "border-[#D1D5DB]", text: "text-[#1F2937]", icon: "text-[#6B7280]" },
    blue: { bg: "bg-[#EAF0F5]", border: "border-[#C8D5E2]", text: "text-[#2F5D8A]", icon: "text-[#3B6D9C]" },
    emerald: { bg: "bg-[#EDF3EC]", border: "border-[#C5D9C1]", text: "text-[#4A7A56]", icon: "text-[#6B8F62]" },
  };
  const c = colorMap[color] || colorMap.white;

  return (
    <div className={`${c.bg} rounded-lg border ${c.border} p-4 flex items-center justify-between`} data-testid={testId}>
      <div>
        <p className="text-[#6B7280] text-xs font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${c.text}`}>{value}</p>
      </div>
      <Icon className={`h-8 w-8 ${c.icon} opacity-50`} />
    </div>
  );
};

/* Exception / Tracking Card */
const ExceptionCard = ({ label, total, today, icon: Icon, color, onClick, testId }) => {
  const colorMap = {
    red: { bg: "bg-[#F3ECEC]", border: "border-[#DFC4C4]", text: "text-[#8B4545]", num: "text-[#7A3B3B]", todayBg: "bg-[#E8D4D4]" },
    amber: { bg: "bg-[#F5F0E6]", border: "border-[#DDD3BF]", text: "text-[#9A7A3E]", num: "text-[#7A6535]", todayBg: "bg-[#EDE5D6]" },
    cyan: { bg: "bg-[#EAF0F5]", border: "border-[#C8D5E2]", text: "text-[#2F5D8A]", num: "text-[#2F5D8A]", todayBg: "bg-[#DCE4ED]" },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div
      className={`${c.bg} rounded-lg border ${c.border} p-4 ${onClick ? "cursor-pointer hover:brightness-110" : ""} transition-all`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${c.text}`} />
        <p className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>{label}</p>
      </div>
      <div className="flex items-end justify-between">
        <p className={`text-2xl font-bold ${total > 0 ? c.num : "text-gray-400"}`}>{total}</p>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${today > 0 ? `${c.todayBg} ${c.text}` : "bg-gray-100 text-gray-400"}`}>
          Today: {today}
        </span>
      </div>
    </div>
  );
};

/* Summary Card Component */
const SummaryCard = ({ title, icon: Icon, color, total, items, onClick, testId }) => {
  const colorMap = {
    amber: { bg: "bg-[#F5F0E6]", border: "border-[#DDD3BF]", text: "text-[#9A7A3E]", icon: "text-[#B08A3E]", badge: "bg-[#EDE5D6] text-[#7A6535]" },
    blue: { bg: "bg-[#EAF0F5]", border: "border-[#C8D5E2]", text: "text-[#2F5D8A]", icon: "text-[#3B6D9C]", badge: "bg-[#DCE4ED] text-[#2F5D8A]" },
    cyan: { bg: "bg-[#EAF0F5]", border: "border-[#C8D5E2]", text: "text-[#2F5D8A]", icon: "text-[#3B6D9C]", badge: "bg-[#DCE4ED] text-[#2F5D8A]" },
    emerald: { bg: "bg-[#EDF3EC]", border: "border-[#C5D9C1]", text: "text-[#4A7A56]", icon: "text-[#6B8F62]", badge: "bg-[#D8E8D4] text-[#4A7A56]" },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div
      className={`${c.bg} rounded-lg border ${c.border} p-4 cursor-pointer hover:brightness-110 transition-all group`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4.5 w-4.5 ${c.icon}`} />
          <h3 className={`text-sm font-semibold ${c.text} uppercase tracking-wide`}>{title}</h3>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
      </div>
      <p className="text-2xl font-bold text-gray-800 mb-3">{total}</p>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className={item.highlight ? "text-[#8B4545] font-medium" : "text-gray-500"}>{item.label}</span>
            <span className={`px-1.5 py-0.5 rounded-full font-semibold ${item.highlight ? "bg-[#E8D4D4] text-[#7A3B3B]" : c.badge}`}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Attention Badge */
const AttentionBadge = ({ title, count, icon: Icon, color, description, testId }) => {
  const colorMap = {
    amber: { bg: "bg-[#F5F0E6]", border: "border-[#DDD3BF]", text: "text-[#9A7A3E]", num: "text-[#7A6535]" },
    orange: { bg: "bg-[#F5F0E6]", border: "border-[#DDD3BF]", text: "text-[#9A7A3E]", num: "text-[#7A6535]" },
    rose: { bg: "bg-[#F3ECEC]", border: "border-[#DFC4C4]", text: "text-[#8B4545]", num: "text-[#7A3B3B]" },
    violet: { bg: "bg-[#EEEDF3]", border: "border-[#D5CFE3]", text: "text-[#5A4F7A]", num: "text-[#5A4F7A]" },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div className={`${c.bg} rounded-lg border ${c.border} p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${c.text}`} />
        <h3 className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>{title}</h3>
      </div>
      <p className={`text-3xl font-bold ${count > 0 ? c.num : "text-gray-400"}`}>{count}</p>
      <p className="text-gray-400 text-xs mt-1">{description}</p>
    </div>
  );
};

export default DashboardPage;
